/*
 * "Atras" cierra el panel abierto, en vez de salir de la pagina
 *
 * Los drawers del tema se abren cambiando un atributo o una clase del DOM: no
 * dejan ninguna marca en el historial. Asi que el gesto de volver del telefono
 * —que es el que el visitante usa para "cerrar" cualquier cosa que ocupa la
 * pantalla— se lleva la navegacion entera en vez de cerrar el panel.
 *
 * El patron es el de siempre: al abrir se empuja una entrada de historial, y al
 * recibir `popstate` se cierra el panel en lugar de navegar. Cerrar desde la UI
 * (la X, el overlay, Escape) **consume** esa entrada con un `history.back()`, o
 * si no el visitante tendria que tocar Atras una vez de mas despues de cerrar.
 *
 * Uso desde un drawer:
 *
 *   open()  -> IGOverlayHistory.push('mi-drawer', () => close({ fromHistory: true }))
 *   close() -> if (!opts.fromHistory) IGOverlayHistory.pop('mi-drawer')
 *
 * El `fromHistory` es lo que corta la recursion: el cierre que dispara el propio
 * historial no vuelve a pedir un `back()`.
 */
window.IGOverlayHistory = (() => {
  const stack = [];
  let consuming = false;
  let consumingTimer = null;
  let consumingHref = null;

  // `facets.js` escucha `popstate` y decide si re-renderiza la grilla de la PLP
  // comparando `event.state.searchParams`. Una entrada nuestra sin esa clave lo
  // haria re-renderizar con `undefined` y perder los filtros, asi que se arrastra
  // el valor que la URL ya tiene y su comparacion da temprano.
  const stateFor = (id) => {
    const base = history.state && typeof history.state === 'object' ? history.state : {};
    const next = Object.assign({}, base, { igOverlay: id });
    if (next.searchParams === undefined) next.searchParams = window.location.search.slice(1);
    return next;
  };

  // La entrada lleva un fragmento propio en vez de repetir la URL actual.
  // `pushState` sin tercer argumento crea una entrada con la MISMA URL, y Safari
  // en iOS es conocido por colapsar o saltear ese tipo de entrada en el swipe-back
  // desde el borde. El fragmento la hace inequivoca; se elige con prefijo `ig-`
  // para que no matchee ningun id del DOM y el navegador no intente scrollear a el.
  const hashFor = (id) => '#ig-' + id;
  const stripHash = (href) => href.replace(/#ig-[^#]*$/, '');

  const push = (id, close) => {
    if (typeof close !== 'function') return;
    if (stack.some((entry) => entry.id === id)) return;
    stack.push({ id, close });
    try {
      history.pushState(stateFor(id), '', hashFor(id));
    } catch (e) {
      stack.pop();
    }
  };

  const pop = (id) => {
    const index = stack.map((entry) => entry.id).lastIndexOf(id);
    if (index === -1) return;
    stack.splice(index, 1);
    consuming = true;
    // La URL que el visitante tiene que conservar al volver. Hace falta porque
    // `global.js:2447` hace `replaceState` con `?variant=` al cambiar de variante,
    // y el picker de talles del panel de disponibilidad dispara justo eso: sin
    // esto, cerrar con la X revertia el `?variant=` que el visitante acababa de
    // elegir, dejando la URL diciendo una cosa y la ficha otra.
    consumingHref = stripHash(window.location.href);
    // Red de seguridad: si por lo que sea ese `back()` no dispara `popstate`, la
    // bandera no puede quedar puesta o se comeria el proximo Atras de verdad.
    window.clearTimeout(consumingTimer);
    consumingTimer = window.setTimeout(() => {
      consuming = false;
      consumingHref = null;
    }, 500);
    history.back();
  };

  window.addEventListener('popstate', () => {
    if (consuming) {
      consuming = false;
      window.clearTimeout(consumingTimer);
      if (consumingHref && window.location.href !== consumingHref) {
        try {
          history.replaceState(history.state, '', consumingHref);
        } catch (e) {}
      }
      consumingHref = null;
      return;
    }
    const entry = stack.pop();
    if (!entry) return;
    try {
      entry.close({ fromHistory: true });
    } catch (e) {}
  });

  return {
    push,
    pop,
    isOpen: (id) => stack.some((entry) => entry.id === id),
  };
})();
